/**
 * Pure option helpers for `Select`: grouping, search filtering, keyboard stepping
 * that skips disabled rows, and type-ahead matching.
 */

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
  /** Quiet note on the right of the row (not searched, not shown on the trigger). */
  hint?: string
}

export interface SelectGroup {
  label: string
  options: SelectOption[]
}

export type SelectOptions = SelectOption[] | SelectGroup[]

/** How the search box matches option labels. */
export type SelectMatchMode = 'startsWith' | 'includes'

export function isGroupedOptions(options: SelectOptions): options is SelectGroup[] {
  const first = options[0]
  return first !== undefined && 'options' in first
}

export function flattenOptions(options: SelectOptions): SelectOption[] {
  return isGroupedOptions(options) ? options.flatMap((group) => group.options) : options
}

/** Keeps options whose label matches the query (case-insensitive); empty groups are dropped. */
export function filterOptions(options: SelectOptions, query: string, matchMode: SelectMatchMode): SelectOptions {
  const q = query.trim().toLowerCase()
  if (!q) return options
  const matches = (opt: SelectOption) => {
    const label = opt.label.toLowerCase()
    return matchMode === 'includes' ? label.includes(q) : label.startsWith(q)
  }
  if (isGroupedOptions(options)) {
    return options
      .map((group) => ({ ...group, options: group.options.filter(matches) }))
      .filter((group) => group.options.length > 0)
  }
  return options.filter(matches)
}

/**
 * Value of the next enabled option from `fromValue` going `step` (+1 / -1).
 * Stays put at either end (no wrap). With no current value, ArrowDown lands on the
 * first enabled row and ArrowUp on the last.
 */
export function stepEnabledValue(list: SelectOption[], fromValue: string | null, step: 1 | -1): string | null {
  const from = fromValue === null ? -1 : list.findIndex((o) => o.value === fromValue)
  const start = from === -1 ? (step === 1 ? -1 : list.length) : from
  for (let i = start + step; i >= 0 && i < list.length; i += step) {
    const option = list[i]
    if (option && !option.disabled) return option.value
  }
  return from === -1 ? null : fromValue
}

export function firstEnabledValue(list: SelectOption[]): string | null {
  return list.find((o) => !o.disabled)?.value ?? null
}

export function lastEnabledValue(list: SelectOption[]): string | null {
  for (let i = list.length - 1; i >= 0; i -= 1) {
    const option = list[i]
    if (option && !option.disabled) return option.value
  }
  return null
}

/**
 * Type-ahead target for `buffer`: the first enabled option whose label starts with it.
 * A one-letter search starts AFTER the current option and wraps (so pressing the same
 * letter again cycles); a longer search may stay on the current option.
 */
export function findTypeaheadValue(list: SelectOption[], buffer: string, currentValue: string | null): string | null {
  const q = buffer.toLowerCase()
  if (!q) return null
  const current = currentValue === null ? -1 : list.findIndex((o) => o.value === currentValue)
  const matches = list
    .map((option, index) => ({ option, index }))
    .filter(({ option }) => !option.disabled && option.label.toLowerCase().startsWith(q))
  if (matches.length === 0) return null
  const fromNext = q.length === 1
  const pick = matches.find(({ index }) => (fromNext ? index > current : index >= current)) ?? matches[0]
  return pick?.option.value ?? null
}
